import { FormController } from "$root/third-party-integrations/react-hook-form";
import { Badge, ButtonGroupRadio, Column, Row, type StylableProps, Text } from "@mdotm/shadcn/legacy/components";
import { FormField, Select } from "@mdotm/shadcn/mdotm/components";
import { overrideClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { useMemo, type ReactNode } from "react";
import type { Control, Path } from "react-hook-form";
import { z } from "zod";
import { userClassifications$ } from "../instruments/user-classifications";

export function AvailableSoonOverlay({
	children,
	hideBadge = false,
	style,
	className,
}: StylableProps & {
	children: ReactNode;
	/** @default false */ /** @default false */ hideBadge?: boolean;
}): React.JSX.Element {
	return (
		<div className={overrideClassName("relative z-0", className)} style={style}>
			{children}
			{!hideBadge && (
				<div className="-top-3 right-0 absolute">
					<AvailableSoonBadge />
				</div>
			)}
		</div>
	);
}
export function AvailableSoonBadge(): React.JSX.Element {
	return (
		<Badge
			backgroundColor={themeCSSVars.palette_N200}
			color={themeCSSVars.palette_N0}
			size="x-small"
			className="scale-90"
		>
			Available soon
		</Badge>
	);
}

export function DataSourceSection({
	commentaryModelType,
	onChangeDataSource,
	tagUUID,
	onChangeClassificationUuid,
	riskModelDescription,
	customClassificationDescription,
	isCustomComingSoon = false,
	isClassificationSelectionComingSoon = false,
	error,
}: {
	commentaryModelType: "RISK_MODEL" | "TAG";
	onChangeDataSource(v: "RISK_MODEL" | "TAG"): void;
	tagUUID: string | null;
	onChangeClassificationUuid(v: string | null): void;
	riskModelDescription?: string;
	customClassificationDescription?: string;
	/** @default false */ isCustomComingSoon?: boolean;
	/** @default false */ isClassificationSelectionComingSoon?: boolean;
	error?: ReactNode;
}): React.JSX.Element {
	const userClassifications = useAtomValue(userClassifications$);
	const tagClassifications = useMemo(
		() =>
			userClassifications.flatMap((c) =>
				c.fieldType !== "TAG"
					? []
					: [
							{
								label: c.name!,
								value: c.classificationUuid!,
								// group: c.fieldType!,
							},
						],
			),
		[userClassifications],
	);

	return (
		<Column flexGrow={0} gap={16}>
			<Row flexGrow={0} gap={8}>
				<Text type="Body/L/Bold">Select data source</Text>
				{/* <InfoTooltip> // TODO: uncomment when the article is ready
					<TooltipContent>
						<Text type="Body/M/Book">Get to know more about data sources. &nbsp;</Text>
						<ActionText
							type="Body/S/Book"
							onClick={() =>
								// TODO: change article
								Intercom.showArticle("createOrOptimisePortfolio")
							}
						>
							Read more.
						</ActionText>
					</TooltipContent>
				</InfoTooltip> */}
			</Row>
			<Row flexGrow={0} className="w-full">
				<AvailableSoonOverlay className="w-full" hideBadge={!isCustomComingSoon}>
					<ButtonGroupRadio
						className="w-full"
						value={commentaryModelType}
						onChange={onChangeDataSource}
						size="x-small"
						options={[
							{
								className: "w-1/2 justify-center",
								"data-qualifier": `DataSourceSelector/RISK_MODEL`,
								value: "RISK_MODEL",
								children: "Risk model",
							},
							{
								className: "w-1/2 justify-center",
								"data-qualifier": `DataSourceSelector/TAG`,
								value: "TAG",
								children: "Tags",
								disabled: isCustomComingSoon,
							},
						]}
					/>
				</AvailableSoonOverlay>
			</Row>
			{riskModelDescription && commentaryModelType === "RISK_MODEL" && (
				<Row>
					<Text type="Body/M/Book">{riskModelDescription}</Text>
				</Row>
			)}
			{customClassificationDescription && commentaryModelType === "TAG" && (
				<Row>
					<Text type="Body/M/Book">{customClassificationDescription}</Text>
				</Row>
			)}
			{commentaryModelType === "TAG" && (
				<FormField label="Select Classification" error={error}>
					{({ id, invalid }) => (
						<AvailableSoonOverlay className="w-full" hideBadge={!isClassificationSelectionComingSoon}>
							<Select
								enableSearch
								className="w-full"
								id={id}
								invalid={invalid}
								options={tagClassifications}
								value={tagUUID}
								onChange={onChangeClassificationUuid}
								disabled={isClassificationSelectionComingSoon}
							/>
						</AvailableSoonOverlay>
					)}
				</FormField>
			)}
		</Column>
	);
}

export type DataSourceFormFieldValues = {
	commentaryModelType: "RISK_MODEL" | "TAG";
	tagUUID: string | null;
};

export function DataSourceFormSection<T extends DataSourceFormFieldValues>({
	control,
	riskModelDescription,
	customClassificationDescription,
	isCustomComingSoon,
	isClassificationSelectionComingSoon,
}: {
	control: Control<T, any>;
	riskModelDescription?: string;
	customClassificationDescription?: string;
	isCustomComingSoon?: boolean;
	isClassificationSelectionComingSoon?: boolean;
}): React.JSX.Element {
	return (
		<FormController
			control={control}
			name={"commentaryModelType" as Path<T>} // just needed a little nudge, the specified keys are guaranteed to exist by the above "extends" requirement
			render={({ field: dataSourceField }) => (
				<FormController
					control={control}
					name={"tagUUID" as Path<T>} // just needed a little nudge, the specified keys are guaranteed to exist by the above "extends" requirement
					render={({ field: classificationField, fieldState }) => (
						<DataSourceSection
							tagUUID={classificationField.value}
							onChangeClassificationUuid={classificationField.onChange}
							commentaryModelType={
								(dataSourceField.value as DataSourceFormFieldValues["commentaryModelType"] | undefined) ??
								("RISK_MODEL" as const)
							}
							onChangeDataSource={dataSourceField.onChange}
							riskModelDescription={riskModelDescription}
							customClassificationDescription={customClassificationDescription}
							isCustomComingSoon={isCustomComingSoon}
							isClassificationSelectionComingSoon={isClassificationSelectionComingSoon}
							error={fieldState.error?.message}
						/>
					)}
				/>
			)}
		/>
	);
}

export const dataSourceValidation = z
	.object({
		commentaryModelType: z.enum(["RISK_MODEL", "TAG"]),
		tagUUID: z.string().optional().nullable(),
	})
	.passthrough()
	.refine(({ commentaryModelType, tagUUID }) => commentaryModelType === "RISK_MODEL" || tagUUID != null, {
		path: ["tagUUID"],
		message: "Please select a classification",
	});
